import { useState, useRef, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { authApi, uploadApi, voiceSessionApi, apiKeysApi } from '../services/api'
import { mediaUrl } from '../services/config'
import type { ApiKey } from '../services/api'
import { Camera, Check, Loader2, ZoomIn, Move, Headphones, User, Bell, Lock, Activity, KeyRound, Plus, Trash2, Ban, BookOpen, ExternalLink, Monitor } from 'lucide-react'
import { SessionsSection } from '../components/settings/SessionsSection'
import TokenRevealModal from '../components/integrations/TokenRevealModal'
import ApiKeyCreateModal from '../components/integrations/ApiKeyCreateModal'
import { PageShell } from '../components/layout/PageShell'
import { PageBrow } from '../components/layout/PageBrow'
import { SecondaryRail, RailItem } from '../components/layout/SecondaryRail'
import { SectionCard } from '../components/patterns'
import { DataTable } from '../components/patterns'
import type { Column } from '../components/patterns'
import NotificationsSettingsSection from '../components/settings/NotificationsSettingsSection'
import PhoneVerificationField from '../components/settings/PhoneVerificationField'
import TwoFactorSection from '../components/settings/TwoFactorSection'
import { displayInitial } from '../utils/displayInitial'
import { telephonyApi } from '../services/telephony'
import { formatDuration } from '../utils/formatDuration'

type Tab = 'profile' | 'notifications' | 'security' | 'voice' | 'api-keys' | 'sessions'

const EDITOR_SIZE = 280
const OUTPUT_SIZE = 512

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'profile', label: 'Profile', icon: <User size={14} /> },
  { id: 'notifications', label: 'Notifications', icon: <Bell size={14} /> },
  { id: 'security', label: 'Security', icon: <Lock size={14} /> },
  { id: 'voice', label: 'Voice', icon: <Activity size={14} /> },
  { id: 'api-keys', label: 'API', icon: <KeyRound size={14} /> },
  { id: 'sessions', label: 'Sessions', icon: <Monitor size={14} /> },
]

export default function UserProfilePage() {
  const user = useAuthStore((s) => s.user)
  const updateUser = useAuthStore((s) => s.updateUser)
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = (searchParams.get('tab') as Tab | null) ?? 'profile'
  const [username, setUsername] = useState(user?.username || '')
  const [firstName, setFirstName] = useState(user?.firstName || '')
  const [lastName, setLastName] = useState(user?.lastName || '')
  const [position, setPosition] = useState(user?.position || '')
  const [aboutMe, setAboutMe] = useState(user?.aboutMe || '')
  const [customStatus, setCustomStatus] = useState(user?.customStatus || '')
  const [status, setStatus] = useState(user?.status || 'ONLINE')
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl || '')
  const [birthday, setBirthday] = useState(user?.birthday ? user.birthday.slice(0, 10) : '')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')

  // Avatar editor state
  const [editorOpen, setEditorOpen] = useState(false)
  const [editorSrc, setEditorSrc] = useState<string | null>(null)
  const [editorSize, setEditorSize] = useState({ w: 0, h: 0 })
  const [editorScale, setEditorScale] = useState(1)
  const [editorOffset, setEditorOffset] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const dragStartRef = useRef({ mx: 0, my: 0, ox: 0, oy: 0 })

  const notifSettings = (user?.settings ?? {}) as Record<string, any>
  const [notifSound, setNotifSound] = useState(notifSettings.notifSound !== false)
  const [notifDesktop, setNotifDesktop] = useState(notifSettings.notifDesktop !== false)
  const [notifEmail, setNotifEmail] = useState(notifSettings.notifEmail === true)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')

  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (user) {
      setUsername(user.username || '')
      setFirstName(user.firstName || '')
      setLastName(user.lastName || '')
      setPosition(user.position || '')
      setAboutMe(user.aboutMe || '')
      setCustomStatus(user.customStatus || '')
      setStatus(user.status || 'ONLINE')
      setAvatarUrl(user.avatarUrl || '')
      setBirthday(user.birthday ? user.birthday.slice(0, 10) : '')
      const s = (user.settings ?? {}) as Record<string, any>
      setNotifSound(s.notifSound !== false)
      setNotifDesktop(s.notifDesktop !== false)
      setNotifEmail(s.notifEmail === true)
    }
  }, [user?.id])

  const openEditor = useCallback((file: File) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      setEditorSrc(url)
      setEditorSize({ w: img.naturalWidth, h: img.naturalHeight })
      const startScale = (EDITOR_SIZE / Math.min(img.naturalWidth, img.naturalHeight)) * 1.2
      setEditorScale(startScale)
      setEditorOffset({ x: 0, y: -20 })
      setEditorOpen(true)
    }
    img.src = url
  }, [])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    openEditor(file)
    e.target.value = ''
  }

  const handleApplyCrop = async () => {
    if (!editorSrc) return
    setSaving(true)
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const i = new Image()
        i.onload = () => resolve(i)
        i.onerror = reject
        i.src = editorSrc
      })

      const canvas = document.createElement('canvas')
      canvas.width = OUTPUT_SIZE
      canvas.height = OUTPUT_SIZE
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('No 2d context')

      ctx.beginPath()
      ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2)
      ctx.clip()

      const ratio = OUTPUT_SIZE / EDITOR_SIZE
      const drawScale = editorScale * ratio
      const drawW = img.naturalWidth * drawScale
      const drawH = img.naturalHeight * drawScale
      const drawX = OUTPUT_SIZE / 2 + editorOffset.x * ratio - drawW / 2
      const drawY = OUTPUT_SIZE / 2 + editorOffset.y * ratio - drawH / 2

      ctx.drawImage(img, drawX, drawY, drawW, drawH)

      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
      if (!blob) throw new Error('Canvas toBlob failed')

      const file = new File([blob], 'avatar.png', { type: 'image/png' })
      const res = await uploadApi.upload([file])
      const url = res.data?.files?.[0]?.fileUrl
      if (url) {
        setAvatarUrl(url)
        await updateUser({ avatarUrl: url })
        setMessage('Avatar updated')
      }
      setEditorOpen(false)
      setEditorSrc(null)
    } catch {
      setMessage('Failed to crop avatar')
    } finally {
      setSaving(false)
      setTimeout(() => setMessage(''), 2000)
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsDragging(true)
    dragStartRef.current = { mx: e.clientX, my: e.clientY, ox: editorOffset.x, oy: editorOffset.y }
  }

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return
    const { mx, my, ox, oy } = dragStartRef.current
    setEditorOffset({ x: ox + (e.clientX - mx), y: oy + (e.clientY - my) })
  }, [isDragging])

  const handleMouseUp = useCallback(() => setIsDragging(false), [])

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  const handleSaveProfile = async () => {
    setSaving(true)
    try {
      await updateUser({
        username: username.trim() || undefined,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || null,
        position: position.trim() || null,
        aboutMe: aboutMe.trim() || null,
        customStatus: customStatus.trim() || null,
        status,
        avatarUrl: avatarUrl || null,
        birthday: birthday || null,
      } as any)
      setMessage('Profile saved')
    } catch {
      setMessage('Failed to save profile')
    } finally {
      setSaving(false)
      setTimeout(() => setMessage(''), 2000)
    }
  }

  const handleSaveNotifications = async () => {
    setSaving(true)
    try {
      await updateUser({
        settings: { ...notifSettings, notifSound, notifDesktop, notifEmail },
      })
      setMessage('Notification settings saved')
    } catch {
      setMessage('Failed to save settings')
    } finally {
      setSaving(false)
      setTimeout(() => setMessage(''), 2000)
    }
  }

  const handleChangePassword = async () => {
    setPasswordError('')
    setPasswordSuccess('')
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters')
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }
    setSaving(true)
    try {
      await authApi.changePassword({ currentPassword, newPassword })
      setPasswordSuccess('Password changed successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: any) {
      setPasswordError(err.response?.data?.error || 'Failed to change password')
    } finally {
      setSaving(false)
    }
  }

  const statusOptions = [
    { value: 'ONLINE', label: 'Online', color: '#3ba55d' },
    { value: 'IDLE', label: 'Idle', color: '#faa61a' },
    { value: 'DO_NOT_DISTURB', label: 'Do Not Disturb', color: '#ed4245' },
    { value: 'INVISIBLE', label: 'Invisible', color: '#72767d' },
  ]

  const activeTabDef = TABS.find((t) => t.id === activeTab) ?? TABS[0]

  const rail2 = (
    <SecondaryRail title="Settings">
      {TABS.map((t) => (
        <RailItem
          key={t.id}
          active={activeTab === t.id}
          icon={t.icon}
          label={t.label}
          onClick={() =>
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev)
              next.set('tab', t.id)
              return next
            })
          }
        />
      ))}
    </SecondaryRail>
  )

  const brow = (
    <PageBrow
      left={
        <h1 className="text-base font-semibold text-foreground">{activeTabDef.label}</h1>
      }
      right={message ? <span className="text-xs text-emerald-500">{message}</span> : undefined}
    />
  )

  return (
    <PageShell rail2={rail2} brow={brow}>
      <div className="mx-auto max-w-3xl p-6 space-y-6">
        {activeTab === 'profile' && (
          <>
            {/* Avatar banner — kept outside SectionCard so overflow-visible works for the crop editor canvas */}
            <SectionCard title="Profile" className="overflow-visible">
              {/* Avatar banner */}
              <div className="relative">
                <div className="h-24 bg-gradient-to-r from-primary to-primary rounded-lg" />
                <div className="absolute -bottom-8 left-4">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-full bg-card p-1 overflow-hidden">
                      {avatarUrl ? (
                        <img src={mediaUrl(avatarUrl)} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <div className="w-full h-full rounded-full bg-primary flex items-center justify-center text-foreground text-xl font-bold">
                          {displayInitial(username)}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="absolute bottom-0 right-0 w-7 h-7 bg-card rounded-full flex items-center justify-center border border-border text-muted-foreground hover:text-foreground transition"
                      title="Change avatar"
                    >
                      <Camera size={14} />
                    </button>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                  </div>
                </div>
              </div>

              {editorOpen && editorSrc && (
                <div className="pt-8 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-foreground">Crop avatar</h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setEditorOpen(false); setEditorSrc(null) }}
                        className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground bg-muted rounded transition"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleApplyCrop}
                        disabled={saving}
                        className="px-3 py-1.5 text-xs text-foreground bg-green-600 hover:bg-green-700 rounded transition disabled:opacity-50 flex items-center gap-1"
                      >
                        {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                        Apply
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div
                      className={`relative shrink-0 rounded-full overflow-hidden bg-background border-2 border-white/10 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                      style={{ width: EDITOR_SIZE, height: EDITOR_SIZE }}
                      onMouseDown={handleMouseDown}
                    >
                      {editorSrc && (
                        <img
                          src={editorSrc}
                          alt=""
                          draggable={false}
                          className="absolute select-none"
                          style={{
                            left: 0,
                            top: 0,
                            width: editorSize.w,
                            height: editorSize.h,
                            maxWidth: 'none',
                            maxHeight: 'none',
                            transformOrigin: '0 0',
                            transform: `translate(${EDITOR_SIZE / 2 - editorSize.w * editorScale / 2 + editorOffset.x}px, ${EDITOR_SIZE / 2 - editorSize.h * editorScale / 2 + editorOffset.y}px) scale(${editorScale})`,
                          }}
                        />
                      )}
                    </div>

                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Move size={14} />
                        <span>Drag image to position your face</span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><ZoomIn size={12} /> Zoom</span>
                          <span className="font-mono">{Math.round(editorScale * 100)}%</span>
                        </div>
                        <input
                          type="range"
                          min={0.2}
                          max={3}
                          step={0.05}
                          value={editorScale}
                          onChange={(e) => setEditorScale(parseFloat(e.target.value))}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Display name">
                  <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full bg-background text-foreground rounded px-3 py-2 text-sm border border-border focus:outline-none focus:border-primary transition"
                  />
                </Field>
                <Field label="Email">
                  <input
                    value={user?.email || ''}
                    disabled
                    className="w-full bg-background text-muted-foreground rounded px-3 py-2 text-sm border border-border cursor-not-allowed"
                  />
                </Field>
                <Field label="First name">
                  <input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full bg-background text-foreground rounded px-3 py-2 text-sm border border-border focus:outline-none focus:border-primary transition"
                  />
                </Field>
                <Field label="Last name">
                  <input
                    value={lastName ?? ''}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full bg-background text-foreground rounded px-3 py-2 text-sm border border-border focus:outline-none focus:border-primary transition"
                  />
                </Field>
                <Field label="Birthday">
                  <input
                    type="date"
                    value={birthday}
                    onChange={(e) => setBirthday(e.target.value)}
                    className="w-full bg-background text-foreground rounded px-3 py-2 text-sm border border-border focus:outline-none focus:border-primary transition"
                  />
                </Field>
                {/* OSPROD-79: position is admin-set, displayed
                    read-only so the user can see what was assigned
                    but can't self-promote. Department is per-server
                    now — see each server's settings panel. */}
                <Field label="Position / Job title" full>
                  <div className="w-full bg-muted text-muted-foreground rounded px-3 py-2 text-sm border border-border min-h-[38px] flex items-center justify-between">
                    <span className={position ? 'text-foreground' : 'italic'}>
                      {position || 'Not set — ask an admin to assign'}
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">Admin only</span>
                  </div>
                </Field>

                {/* OSPROD-88: read-only PSTN number assigned by an admin. */}
                {user?.id && <ProfilePhoneNumberField userId={user.id} />}

                {/* OSPROD-179: the user's own SMS-verified mobile. */}
                <PhoneVerificationField />
              </div>

              <Field label="Status">
                <div className="flex gap-2 flex-wrap">
                  {statusOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setStatus(opt.value as any)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                        status === opt.value
                          ? 'border-border bg-accent text-foreground'
                          : 'border-transparent bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: opt.color }} />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Custom status">
                <input
                  value={customStatus ?? ''}
                  onChange={(e) => setCustomStatus(e.target.value)}
                  placeholder="What's happening?"
                  maxLength={128}
                  className="w-full bg-background text-foreground rounded px-3 py-2 text-sm border border-border focus:outline-none focus:border-primary transition"
                />
              </Field>

              <Field label="About me">
                <textarea
                  value={aboutMe ?? ''}
                  onChange={(e) => setAboutMe(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  className="w-full bg-background text-foreground rounded px-3 py-2 text-sm border border-border focus:outline-none focus:border-primary transition resize-none"
                />
              </Field>

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded text-sm font-semibold transition disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  Save changes
                </button>
              </div>
            </SectionCard>
          </>
        )}

        {activeTab === 'notifications' && (
          <SectionCard title="Notifications">
            <p className="text-sm text-muted-foreground">Choose how you want to be notified.</p>

            <ToggleRow
              checked={notifSound}
              onChange={setNotifSound}
              title="Sound"
              hint="Play a sound for new messages and calls"
            />
            <ToggleRow
              checked={notifDesktop}
              onChange={setNotifDesktop}
              title="Desktop notifications"
              hint="Show system notifications"
            />
            <ToggleRow
              checked={notifEmail}
              onChange={setNotifEmail}
              title="Email notifications"
              hint="Receive summary emails"
            />

            <button
              onClick={handleSaveNotifications}
              disabled={saving}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded text-sm font-semibold transition disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Save
            </button>

            <div className="border-t border-border pt-6 mt-6">
              <NotificationsSettingsSection />
            </div>
          </SectionCard>
        )}

        {activeTab === 'security' && (
          <SectionCard title="Password &amp; Security" className="max-w-md">
            <p className="text-sm text-muted-foreground">Change your password to keep your account secure.</p>

            <Field label="Current password">
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full bg-background text-foreground rounded px-3 py-2 text-sm border border-border focus:outline-none focus:border-primary transition"
              />
            </Field>
            <Field label="New password">
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-background text-foreground rounded px-3 py-2 text-sm border border-border focus:outline-none focus:border-primary transition"
              />
            </Field>
            <Field label="Confirm new password">
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-background text-foreground rounded px-3 py-2 text-sm border border-border focus:outline-none focus:border-primary transition"
              />
            </Field>

            {passwordError && <p className="text-destructive text-sm">{passwordError}</p>}
            {passwordSuccess && <p className="text-green-500 text-sm">{passwordSuccess}</p>}

            <button
              onClick={handleChangePassword}
              disabled={saving || !currentPassword || !newPassword || !confirmPassword}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded text-sm font-semibold transition disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Change password
            </button>

            {/* OSPROD-180: SMS two-factor authentication. */}
            <TwoFactorSection />
          </SectionCard>
        )}

        {activeTab === 'voice' && (
          <div className="space-y-6">
            <SectionCard title="Voice settings">
              <VoiceSettingsSection />
            </SectionCard>
            <VoiceHistoryCard />
          </div>
        )}

        {activeTab === 'api-keys' && <ApiKeysTab />}

        {activeTab === 'sessions' && (
          <div className="space-y-6">
            <SessionsSection />
            <div className="space-y-2">
              <h2 className="text-sm font-semibold text-foreground">Other connections</h2>
              <button
                onClick={() => setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('tab', 'security'); return next })}
                className="flex w-full items-center justify-between rounded-lg border border-border bg-card px-3 py-2.5 text-left hover:bg-muted transition duration-150"
              >
                <span className="text-sm text-foreground">Trusted devices (2FA)</span>
                <span className="text-xs text-muted-foreground">Manage in Security ›</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  )
}

function Field({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <label className="block text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">{label}</label>
      {children}
    </div>
  )
}

// OSPROD-88: read-only display of the user's assigned PSTN number.
// Hidden if no number is assigned. Admin sets the number from the
// Admin → Users → user-edit modal.
function ProfilePhoneNumberField({ userId }: { userId: string }) {
  const [phone, setPhone] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    telephonyApi.users.getConfig(userId).then((cfg) => {
      if (!cancelled) setPhone(cfg?.phoneNumber ?? null)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [userId])
  if (!phone) return null
  return (
    <Field label="My phone number" full>
      <div className="w-full bg-muted text-foreground rounded px-3 py-2 text-sm border border-border min-h-[38px] flex items-center justify-between">
        <span>{phone}</span>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">Admin only</span>
      </div>
    </Field>
  )
}

function ToggleRow({
  checked,
  onChange,
  title,
  hint,
}: { checked: boolean; onChange: (v: boolean) => void; title: string; hint: string }) {
  return (
    <label className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 hover:bg-accent transition cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-primary rounded"
      />
      <div>
        <div className="text-sm text-foreground font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
    </label>
  )
}

function VoiceSettingsSection() {
  // OSPROD-104: per-user "ask before switching voice channels" preference.
  // Stored as skipVoiceSwitchConfirm (default false = ask); shown
  // positively here so checked = ask. Reads straight from user.settings so
  // it reflects the optimistic update; the authStore action persists it.
  const skipConfirm = useAuthStore((s) => s.user?.settings?.skipVoiceSwitchConfirm ?? false)
  const setSkipVoiceSwitchConfirm = useAuthStore((s) => s.setSkipVoiceSwitchConfirm)
  return (
    <ToggleRow
      checked={!skipConfirm}
      onChange={(v) => setSkipVoiceSwitchConfirm(!v)}
      title="Ask before switching voice channels"
      hint="Show a confirmation when you click a voice channel while already in another call. Turn off to switch instantly."
    />
  )
}

type VoiceSession = {
  id: string
  startedAt: string
  endedAt: string | null
  duration: number | null
  channel?: { name?: string } | null
  server?: { name?: string } | null
}

const voiceHistoryColumns: Column<VoiceSession>[] = [
  {
    key: 'channel',
    header: 'Channel',
    render: (s) => (
      <span className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
          <Headphones size={13} className="text-primary" />
        </span>
        <span className="text-sm text-foreground truncate">{s.channel?.name || 'Unknown channel'}</span>
      </span>
    ),
  },
  {
    key: 'server',
    header: 'Server',
    render: (s) => (
      <span className="text-sm text-muted-foreground">{s.server?.name || '—'}</span>
    ),
  },
  {
    key: 'date',
    header: 'Date',
    render: (s) => (
      <span className="text-[10px] text-muted-foreground whitespace-nowrap">
        {new Date(s.startedAt).toLocaleString()}
        {' — '}
        {s.endedAt ? new Date(s.endedAt).toLocaleTimeString() : 'Active'}
      </span>
    ),
  },
  {
    key: 'duration',
    header: 'Duration',
    align: 'right',
    render: (s) => (
      <span className="text-xs text-muted-foreground font-mono">{formatDuration(s.duration || 0)}</span>
    ),
  },
]

function VoiceHistoryCard() {
  const user = useAuthStore((s) => s.user)
  const [sessions, setSessions] = useState<VoiceSession[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return
    setLoading(true)
    voiceSessionApi
      .userSessions(user.id)
      .then((res) => setSessions(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [user?.id])

  return (
    <SectionCard title="Voice history" noBodyPadding>
      <DataTable<VoiceSession>
        columns={voiceHistoryColumns}
        rows={sessions}
        rowKey={(s) => s.id}
        empty={
          loading
            ? <div className="text-muted-foreground text-center py-8">Loading voice history…</div>
            : <div className="text-muted-foreground text-center py-8 text-sm">No voice sessions recorded yet.</div>
        }
      />
    </SectionCard>
  )
}

const BASE_URL = window.location.origin

function ApiKeysTab() {
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [revealToken, setRevealToken] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    apiKeysApi.list().then((r) => setKeys(r.data)).catch(() => {}).finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  const revoke = async (k: ApiKey) => { await apiKeysApi.revoke(k.id); load() }
  const remove = async (k: ApiKey) => { await apiKeysApi.remove(k.id); load() }

  const columns: Column<ApiKey>[] = [
    {
      key: 'name', header: 'Name',
      render: (k) => (
        <span className="flex flex-col">
          <span className="text-sm text-foreground font-medium">{k.name}</span>
          <code className="text-[11px] text-muted-foreground">{k.prefix}...</code>
        </span>
      ),
    },
    { key: 'scopes', header: 'Scopes', render: (k) => <span className="text-xs text-muted-foreground">{k.scopes.join(', ')}</span> },
    { key: 'lastUsed', header: 'Last used', render: (k) => <span className="text-xs text-muted-foreground">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '-'}</span> },
    { key: 'status', header: 'Status', render: (k) => k.revokedAt ? <span className="text-xs text-destructive">Revoked</span> : <span className="text-xs text-emerald-500">Active</span> },
    {
      key: 'actions', header: '', align: 'right',
      render: (k) => (
        <span className="flex items-center justify-end gap-2">
          {!k.revokedAt && <button onClick={() => revoke(k)} title="Revoke" className="text-muted-foreground hover:text-foreground"><Ban size={15} /></button>}
          <button onClick={() => remove(k)} title="Delete" className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <SectionCard title="Documentation">
        <p className="text-sm text-muted-foreground">
          Full API reference and interactive console — no repository access needed. More guides coming soon.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          <a
            href={`${BASE_URL}/api/v1/docs`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md"
          >
            <BookOpen size={14} /> Open API docs (Swagger)
          </a>
          <a
            href={`${BASE_URL}/api/v1/openapi.json`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-muted text-foreground rounded-md hover:bg-accent"
          >
            <ExternalLink size={14} /> OpenAPI spec (JSON)
          </a>
        </div>
      </SectionCard>

      <SectionCard
        title="API Keys"
        action={
          <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md">
            <Plus size={14} /> Create key
          </button>
        }
        noBodyPadding
      >
        <DataTable<ApiKey>
          columns={columns}
          rows={keys}
          rowKey={(k) => k.id}
          empty={loading
            ? <div className="text-muted-foreground text-center py-8">Loading...</div>
            : <div className="text-muted-foreground text-center py-8 text-sm">No API keys yet. Create one to post into channels from your scripts.</div>}
        />
      </SectionCard>

      <SectionCard title="Quickstart">
        <p className="text-sm text-muted-foreground">Post a message to a channel (replace the channel id and your key):</p>
        <pre className="bg-muted rounded p-3 text-xs text-foreground overflow-x-auto"><code>{`curl -X POST ${BASE_URL}/api/v1/channels/<channelId>/messages \\
  -H "Authorization: Bearer rx2_pk_..." \\
  -H "Content-Type: application/json" \\
  -d '{"content":"Hello from my script"}'`}</code></pre>
        <p className="text-xs text-muted-foreground">Full reference: <code>docs/api/integrations-api.md</code></p>
      </SectionCard>

      {showCreate && <ApiKeyCreateModal onClose={() => setShowCreate(false)} onCreated={(t) => { setRevealToken(t); load() }} />}
      {revealToken && <TokenRevealModal token={revealToken} onClose={() => setRevealToken(null)} />}
    </div>
  )
}
